import React, { useState, useEffect, useMemo } from 'react';
import { Icons } from '../ui/Icons';
import { db } from '../../services/firebase';
import { ref, onValue, off, query, limitToLast } from 'firebase/database';
import { subscribeToPath } from '../../services/database';
import { Memo, MemoCategory, MemoTopic, MemoTag } from '../../types';
import { safeFormat, safeIsToday, safeIsThisWeek, safeIsThisMonth } from '../../utils/safeDateFns';
import { motion, AnimatePresence } from 'motion/react';
import DOMPurify from 'dompurify';

interface MemosViewProps {
    isAdmin?: boolean;
    staffId?: string;
    staffName?: string;
}

const MEMOS_PER_PAGE = 12;

const MemosView: React.FC<MemosViewProps> = ({ isAdmin = false, staffId = '', staffName = 'Staff' }) => {
    // Data State
    const [memos, setMemos] = useState<Memo[]>([]);
    const [categories, setCategories] = useState<MemoCategory[]>([]);
    const [topics, setTopics] = useState<MemoTopic[]>([]);
    const [tags, setTags] = useState<MemoTag[]>([]);
    
    // UI State
    const [loading, setLoading] = useState(true);
    const [selectedMemoForView, setSelectedMemoForView] = useState<Memo | null>(null);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    
    // Filter State
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [selectedTopic, setSelectedTopic] = useState<string>('');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'thisWeek' | 'thisMonth'>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showTagDropdown, setShowTagDropdown] = useState(false);

    // Subscriptions
    useEffect(() => {
        const unsubCategories = subscribeToPath('categories', (data) => {
            const list = data ? Object.entries(data).map(([id, val]: [string, any]) => ({ ...val, id })) as MemoCategory[] : [];
            setCategories(list.filter(c => c.status !== 'deleted' && c.status !== 'inactive'));
        });
        const unsubTopics = subscribeToPath('topics', (data) => {
            const list = data ? Object.entries(data).map(([id, val]: [string, any]) => ({ ...val, id })) as MemoTopic[] : [];
            setTopics(list.filter(t => t.status !== 'deleted' && t.status !== 'inactive'));
        });
        const unsubTags = subscribeToPath('tags', (data) => {
            const list = data ? Object.entries(data).map(([id, val]: [string, any]) => ({ ...val, id })) as MemoTag[] : [];
            setTags(list.filter(t => t.status !== 'deleted' && t.status !== 'inactive'));
        });

        // Fetch All Active Memos
        const memosRef = ref(db, 'memos');
        const q = query(memosRef, limitToLast(200));
        const unsubMemos = onValue(q, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const list = Object.entries(data).map(([id, val]: [string, any]) => ({ ...val, id })) as Memo[];
                const sorted = list.filter(m => m.status === 'active' || !m.status).sort((a, b) => {
                    const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
                    const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
                    return (isNaN(dateB) ? 0 : dateB) - (isNaN(dateA) ? 0 : dateA);
                });
                setMemos(sorted);
            } else {
                setMemos([]);
            }
            setLoading(false);
        });

        return () => {
            unsubCategories();
            unsubTopics();
            unsubTags();
            off(memosRef);
        };
    }, []);

    // Filter/Search Logic
    const filteredMemos = useMemo(() => {
        return memos.filter(memo => {
            if (selectedCategory && memo.categoryId !== selectedCategory) return false;
            if (selectedTopic && memo.topicId !== selectedTopic) return false;
            if (selectedTags.length > 0) {
                const memoTags = memo.tags || {};
                if (!selectedTags.every(tagId => memoTags[tagId])) return false;
            }
            if (dateFilter !== 'all') {
                const memoDate = new Date(memo.updatedAt || memo.createdAt);
                if (isNaN(memoDate.getTime())) return false;
                if (dateFilter === 'today' && !safeIsToday(memoDate)) return false;
                if (dateFilter === 'thisWeek' && !safeIsThisWeek(memoDate)) return false;
                if (dateFilter === 'thisMonth' && !safeIsThisMonth(memoDate)) return false;
            }
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                return memo.title.toLowerCase().includes(q) || memo.searchIndex.includes(q);
            }
            return true;
        });
    }, [memos, selectedCategory, selectedTopic, selectedTags, searchQuery, dateFilter]);

    // Pagination
    const totalPages = Math.ceil(filteredMemos.length / MEMOS_PER_PAGE);
    const paginatedMemos = useMemo(() => {
        const start = (currentPage - 1) * MEMOS_PER_PAGE;
        return filteredMemos.slice(start, start + MEMOS_PER_PAGE);
    }, [filteredMemos, currentPage]);

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Memo Center</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">Operational updates and staff announcements</p>
            </div>

            {/* Controls */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-4 shadow-sm flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[250px] relative text-gray-900 dark:text-white">
                    <Icons.Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                        className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Search memos..."
                    />
                </div>
                
                <select
                    value={selectedCategory}
                    onChange={e => { setSelectedCategory(e.target.value); setSelectedTopic(''); setCurrentPage(1); }}
                    className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium text-gray-900 dark:text-white"
                >
                    <option value="">All Categories</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>

                <select
                    value={selectedTopic}
                    onChange={e => { setSelectedTopic(e.target.value); setCurrentPage(1); }}
                    className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium text-gray-900 dark:text-white"
                    disabled={!selectedCategory}
                >
                    <option value="">All Topics</option>
                    {topics.filter(t => t.categoryId === selectedCategory).map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>

                <button 
                    onClick={() => setShowTagDropdown(!showTagDropdown)}
                    className="relative flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                    <Icons.Tag className="w-4 h-4" />
                    {selectedTags.length > 0 ? `${selectedTags.length} Tags` : 'Tags'}
                    {showTagDropdown && (
                        <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl z-10 max-h-60 overflow-y-auto p-2">
                            {tags.map(tag => (
                                <label key={tag.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/50 rounded-lg cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedTags.includes(tag.id)}
                                        onChange={() => {
                                            setSelectedTags(prev => prev.includes(tag.id) ? prev.filter(id => id !== tag.id) : [...prev, tag.id]);
                                            setCurrentPage(1);
                                        }}
                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span className="text-xs">{tag.name}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </button>

                <div className="flex bg-gray-100 dark:bg-slate-900 p-1 rounded-xl">
                    <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-lg ${viewMode === 'grid' ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600 dark:text-white' : 'text-gray-400'}`}><Icons.LayoutGrid className="w-4 h-4" /></button>
                    <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg ${viewMode === 'list' ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600 dark:text-white' : 'text-gray-400'}`}><Icons.List className="w-4 h-4" /></button>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <Icons.Loader className="w-10 h-10 animate-spin text-indigo-600 mb-4" />
                    <p className="text-gray-500">Loading memos...</p>
                </div>
            ) : filteredMemos.length === 0 ? (
                <div className="text-center py-20 bg-gray-50 dark:bg-slate-900/50 rounded-3xl border border-dashed border-gray-200 dark:border-slate-700">
                    <Icons.FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">No memos found</h3>
                    <p className="text-gray-500 mt-2">Adjust your filters to see more results</p>
                </div>
            ) : (
                <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"}>
                    {paginatedMemos.map(memo => {
                        const category = categories.find(c => c.id === memo.categoryId);
                        return (
                            <motion.div
                                key={memo.id}
                                layout
                                onClick={() => setSelectedMemoForView(memo)}
                                className={`bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6 shadow-sm hover:shadow-lg transition-all cursor-pointer relative overflow-hidden group ${viewMode === 'list' ? 'flex items-center gap-6' : ''}`}
                            >
                                <div className="absolute top-0 left-0 w-1 h-full" style={{ backgroundColor: category?.color || '#eee' }} />
                                <div className="flex-1">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-indigo-600 transition-colors uppercase">{memo.title}</h3>
                                    <div className="flex items-center gap-4 text-xs text-gray-500">
                                        <span className="flex items-center gap-1"><Icons.User className="w-3 h-3" /> {memo.authorName}</span>
                                        <span className="flex items-center gap-1"><Icons.Calendar className="w-3 h-3" /> {safeFormat(memo.updatedAt || memo.createdAt, 'MMM d, yyyy')}</span>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 pt-8">
                    <button 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg border border-gray-200 dark:border-slate-700 disabled:opacity-50"
                    >
                        <Icons.ChevronLeft className="w-4 h-4 text-gray-600 dark:text-white" />
                    </button>
                    {getPageNumbers().map((n, i) => (
                        <button
                            key={i}
                            onClick={() => typeof n === 'number' && setCurrentPage(n)}
                            disabled={n === '...'}
                            className={`w-10 h-10 rounded-lg text-sm font-bold transition-all ${currentPage === n ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                        >
                            {n}
                        </button>
                    ))}
                    <button 
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg border border-gray-200 dark:border-slate-700 disabled:opacity-50"
                    >
                        <Icons.ChevronRight className="w-4 h-4 text-gray-600 dark:text-white" />
                    </button>
                </div>
            )}

            {/* Preview Modal */}
            <AnimatePresence>
                {selectedMemoForView && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="bg-white dark:bg-slate-800 w-full max-w-4xl max-h-[90vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col border border-gray-200 dark:border-slate-700"
                        >
                            <style>{`
                                .rich-text-content {
                                    overflow-wrap: anywhere;
                                    word-break: break-word;
                                }
                                .rich-text-content table {
                                    border-collapse: collapse;
                                    width: 100% !important;
                                    margin-bottom: 1em;
                                    display: block;
                                    overflow-x: auto;
                                    -webkit-overflow-scrolling: touch;
                                }
                                .rich-text-content table td, .rich-text-content table th {
                                    border: 1px solid #ddd;
                                    padding: 8px;
                                    min-width: 100px;
                                }
                                .dark .rich-text-content table td, .dark .rich-text-content table th {
                                    border-color: #334155;
                                }
                                .rich-text-content img {
                                    max-width: 100%;
                                    height: auto;
                                }
                                .rich-text-content h1 { font-size: 2em; font-weight: bold; margin-bottom: 0.5em; }
                                .rich-text-content h2 { font-size: 1.5em; font-weight: bold; margin-bottom: 0.5em; }
                                .rich-text-content h3 { font-size: 1.25em; font-weight: bold; margin-bottom: 0.5em; }
                                .rich-text-content ul { list-style-type: disc; padding-left: 1.5em; margin-bottom: 1em; }
                                .rich-text-content ol { list-style-type: decimal; padding-left: 1.5em; margin-bottom: 1em; }
                            `}</style>
                            <div className="p-6 border-b border-gray-100 dark:border-slate-700 flex justify-between items-center">
                                <h2 className="text-2xl font-black text-gray-900 dark:text-white uppercase truncate pr-8">{selectedMemoForView.title}</h2>
                                <button onClick={() => setSelectedMemoForView(null)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><Icons.X className="w-6 h-6" /></button>
                            </div>
                            <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 prose dark:prose-invert max-w-none rich-text-content break-words">
                                <div dangerouslySetInnerHTML={{ 
                                    __html: DOMPurify.sanitize(selectedMemoForView.content, {
                                        ADD_TAGS: [
                                            'table', 'tr', 'td', 'th', 'thead', 'tbody', 'tfoot', 'colgroup', 'col', 
                                            'section', 'article', 'aside', 'details', 'summary', 'figure', 'figcaption',
                                            'span', 'div', 'br', 'hr', 'p', 'b', 'strong', 'i', 'em', 'u', 's', 'strike',
                                            'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
                                            'img', 'video', 'a', 'iframe', 'canvas', 'svg', 'path', 'g', 'circle', 'rect',
                                            'style', 'center', 'font'
                                        ],
                                        ADD_ATTR: [
                                            'style', 'colspan', 'rowspan', 'class', 'target', 'rel', 'href', 'src', 
                                            'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'title',
                                            'alt', 'loading', 'data-*', 'border', 'cellpadding', 'cellspacing', 'id',
                                            'align', 'valign', 'bgcolor', 'bordercolor', 'face', 'size', 'color'
                                        ],
                                        FORBID_ATTR: [],
                                        FORBID_TAGS: [],
                                        FORCE_BODY: true,
                                        ALLOW_UNKNOWN_PROTOCOLS: true
                                    }) 
                                }} />
                            </div>
                            <div className="p-6 bg-gray-50 dark:bg-slate-900/50 border-t border-gray-100 dark:border-slate-700 flex justify-between items-center text-xs text-gray-500">
                                <span>Published by <span className="font-bold text-gray-900 dark:text-white">{selectedMemoForView.authorName}</span></span>
                                <span>Updated {safeFormat(selectedMemoForView.updatedAt || selectedMemoForView.createdAt, 'PPpp')}</span>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default MemosView;
